<template>
    <div :id="rootElementId"></div>
</template>

<script>
import * as d3v4 from "d3v4";

  import * as _ from 'lodash';
  import {extent, sum} from 'd3-array';
  import { getDataUrl } from '../../utils/functions';

  export default {
    name: 'PassMatrix',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      passes: {
        type: Array,
        required: false,
      },
      homeTeamScore: {
        type: Number,
        required: false
      },
      rivalTeamScore: {
        type: Number,
        required: false
      },
      teamName: {
        type: String,
        required: false
      },
      rivalTeamName: {
        type: String,
        required: false
      },
      minFrom: {
        type: Number,
        required: false
      },
      minTo: {
        type: Number,
        required: false
      },
      themeDark: {
        type: Boolean,
        required: false
      },
      language: {
        type: String,
        required: false
      },
    },
    computed: {
      rootElementId() {
        return `pitchPassMatrix${this.id}`;
      }
    },
    data() {
      return {
        height: 0,
        width: 0,
      }
    },
    mounted() {

      const SVG_WIDTH = this.size ? this.size : 380;
      const CHART_SIZE_RATIO = SVG_WIDTH / 2 * 0.0022; //CHART_SIZE

      const margin = this.margin ? this.margin : {top: 75, right: 25, bottom: 140, left: 170};
      const width = SVG_WIDTH - margin.left * CHART_SIZE_RATIO - margin.right * CHART_SIZE_RATIO;
      const height = width * 0.67125;

      d3v4.selectAll('.nwTooltip').remove();

      var svg = d3v4
        .select(`#${this.rootElementId}`)
        .append("svg")
        .attr("width", width + margin.left * CHART_SIZE_RATIO + margin.right + CHART_SIZE_RATIO)
        .attr("height", height + margin.top * CHART_SIZE_RATIO + margin.bottom * CHART_SIZE_RATIO)
        .attr("id",`${this.rootElementId}SVG`)
        .append("g")
        .attr("transform", "translate(" + margin.left * CHART_SIZE_RATIO + "," + margin.top * CHART_SIZE_RATIO + ")");

      // Labels of row and columns -> unique identifier of the column called 'playerFromName' and 'playerToName'
      var data = this.passes.reduce(function(acc, d) {
        const found = acc.find(o => o.playerFromName === d.playerToName && o.playerToName === d.playerFromName);

        const newValue = { playerFromName: d.playerToName, playerToName: d.playerFromName, value: 1}
        if (!found && newValue.playerFromName !== newValue.playerToName)
          acc.push(newValue)
        else if (found)
          found.value++;
        return acc;
      }, []);

      var players = [...new Set(data.map(p => p.playerToName))].concat([...new Set(data.map(p => p.playerFromName))])

      players.forEach(function(p1) {
        players.forEach(function(p2) {
          const found = data.find(o => o.playerFromName === p1 && o.playerToName === p2);
          if (!found && p1 !== p2) {
            const emptyRow = { playerFromName: p1, playerToName: p2, value: 0}
            data.push(emptyRow);
          }
        });
      });

      // Build X scales and axis:
      var x = d3v4.scaleBand()
        .range([ 0, width ])
        .domain(players)
        .padding(0.05);

      svg.append("g")
        .style("font-size", 14 * CHART_SIZE_RATIO)
        .style("font-family", "NeueMontreal")
        .style("stroke-width", "1px")
        .style("fill", this.themeDark ? "#fff" : "#000")
        .attr("transform", "translate(0," + height + ")")
        .call(d3v4.axisBottom(x).tickSize(0))
        .selectAll("text")
        .style("text-anchor", "end")
        .style("fill", this.themeDark ? "#fff" : "#000")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-45)")
        .attr('id', function(d) { return "playerTo-" + d.replace(" ", "") })
        .select(".domain")
        .remove()

      // Build Y scales and axis:
      var y = d3v4.scaleBand()
        .range([ height, 0 ])
        .domain(players)
        .padding(0.05);

      svg.append("g")
        .style("font-size", 14 * CHART_SIZE_RATIO)
        .style("font-family", "NeueMontreal")
        .style("fill", this.themeDark ? "#fff" : "#000")
        .call(d3v4.axisLeft(y).tickSize(0))
        .selectAll("text")
        .style("fill", this.themeDark ? "#fff" : "#000")
        .attr('id', function(d) { return "playerFrom-" + d.replace(" ", "") })
        .select(".domain").remove()

      // Build color scale
      var myColor = d3v4.scaleLinear()
      .domain([1, 20])
      .range(['#3366FF', ''])
      .interpolate(d3.interpolateHcl);

      // create a nwTooltip
      var nwTooltip = d3v4.select("body")
        .append("div")
        .style("opacity", 0)
        .attr("class", "nwTooltip")
        .style("background-color", "white")
        .style("border", "solid")
        .style("border-width", 2 * CHART_SIZE_RATIO + "px")
        .style("border-radius", 5 * CHART_SIZE_RATIO + "px")
        .style("padding", 5 * CHART_SIZE_RATIO + "px")

      // Three function that change the nwTooltip when user hover / move / leave a cell
      var mouseover = function(d) {
        nwTooltip
          .style("opacity", 1)
        d3v4.select(this)
          .style("stroke", "black")
          .style("opacity", 1)

        let squareId = document.getElementById("square-" + d.playerFromName.replace(" ", "") + "-" + d.playerToName.replace(" ", "") + "-pitchPassingMatrix");

        nwTooltip
          .style("font-size", 14 * CHART_SIZE_RATIO + "px")
          .html("<b>" + d.value + " Passes</b></br>" + d.playerToName + " to " + d.playerFromName)
          .style("width", 380 * CHART_SIZE_RATIO + "px")
          .style("height", 55 * CHART_SIZE_RATIO + "px")
          .style("left", squareId.getBoundingClientRect().left - (380 * CHART_SIZE_RATIO / 2) + (48 / 2) + "px")
          .style("top", (squareId.getBoundingClientRect().top + window.scrollY - 70 * CHART_SIZE_RATIO) + "px")
          .style("display", "inline-block")


        d3v4.select("#playerFrom-" + d.playerToName.replace(" ", ""))
          .style("font-weight", 700)
          .style("font-size", 16 * CHART_SIZE_RATIO + "px")

        d3v4.select("#playerTo-" + d.playerFromName.replace(" ", ""))
          .style("font-weight", 700)
          .style("font-size", 16 * CHART_SIZE_RATIO + "px")
      }
      var mouseleave = function(d) {
        nwTooltip
          .style("opacity", 0)
        d3v4.select(this)
          .style("stroke", "none")
          .style("opacity", 0.8)

        d3v4.select("#playerFrom-" + d.playerToName.replace(" ", ""))
          .style("font-weight", 400)
          .style("font-size", 14 * CHART_SIZE_RATIO + "px")
        d3v4.select("#playerTo-" + d.playerFromName.replace(" ", ""))
          .style("font-weight", 400)
          .style("font-size",  14 * CHART_SIZE_RATIO + "px")
      }

      // add the squares
      var ssquares = svg.selectAll()
        .data(data, function(d) {return d.playerFromName+':'+d.playerToName;})
        .enter()
        .append("g");

        ssquares
        .append("rect")
          .attr("x", function(d) { return x(d.playerFromName) })
          .attr("y", function(d) { return y(d.playerToName) })
          .attr("rx", 4 * CHART_SIZE_RATIO)
          .attr("ry", 4 * CHART_SIZE_RATIO)
          .attr("width", x.bandwidth() )
          .attr("height", y.bandwidth() )
          .attr('id', function(d) { return "square-" + d.playerFromName.replace(" ", "") + "-" + d.playerToName.replace(" ", "") + "-pitchPassingMatrix"; })
          .style("fill", function(d) { return d.value === 0 ? "#bcbcbc" : myColor(d.value)} )
          .style("stroke-width", 4)
          .style("stroke", "none")
          .style("opacity", 0.8)
        .on("mouseover", mouseover)
        .on("mouseleave", mouseleave)

        ssquares
        .append("text")
        .attr("x", function(d) { return x(d.playerFromName) + 48 * CHART_SIZE_RATIO / 2 })
        .attr("y", function(d) { return y(d.playerToName) + 45 * CHART_SIZE_RATIO / 2})
        .attr("color", function(d) { return d.value > 0 ? "white" : "black"; })
        .style("font-family", "NeueMontreal")
        .style("font-size", 14 * CHART_SIZE_RATIO + "px")
        .attr("font-weight", 500)
        .attr("text-anchor", "middle")
        .style("pointer-events","none")
        .text( function(d) { return d.value; })

      svg.append("text")
        .attr("x", 0)
        .attr("y", -35 * CHART_SIZE_RATIO)
        .attr("text-anchor", "start")
        .attr("dy", "0em")
        .attr("font-weight", 500)
        .style("font-size", 24 * CHART_SIZE_RATIO + "px")
        .style("font-family", "NeueMontreal")
        .html(this.$t('game.passMap.passingMatrix'));

      svg.append("text")
        .attr("x", 0)
        .attr("y", -35 * CHART_SIZE_RATIO)
        .attr("text-anchor", "start")
        .attr("dy", "1.4em")
        .attr("font-weight", 500)
        .style("font-size", 16 * CHART_SIZE_RATIO + "px")
        .style("font-family", "NeueMontreal")
        .html(this.teamName + " " + this.homeTeamScore + " - " +this.rivalTeamScore + " " + this.rivalTeamName);

      svg.append("text")
        .attr("x", -20 * CHART_SIZE_RATIO)
        .attr("y", -10 * CHART_SIZE_RATIO)
        .attr("text-anchor", "end")
        .attr("dy", "0em")
        .attr("font-weight", 700)
        .style("font-size", 16 * CHART_SIZE_RATIO + "px")
        .style("font-family", "NeueMontreal")
        .html("From &#8595;");

      svg.append("text")
        .attr("x", -20 * CHART_SIZE_RATIO)
        .attr("y", 500 * CHART_SIZE_RATIO)
        .attr("text-anchor", "end")
        .attr("font-weight", 700)
        .style("font-size", 16 * CHART_SIZE_RATIO + "px")
        .style("font-family", "NeueMontreal")
        .html("To &#8594;");

      this.drawImage(svg,
        width * 0.61 + margin.left * CHART_SIZE_RATIO,
        - margin.top * CHART_SIZE_RATIO,
        width * 0.19,
        height * 0.19,
        require( this.themeDark ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png")
      );

      //Emit when is loaded
      this.$emit("loaded", this.rootElementId);

    },
    methods: {
      drawImage: function(svg, x, y, width, height, img) {
        svg.append("svg:image")
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
      },
    },
    destroyed(){
      d3v4.selectAll('.nwTooltip').remove();
    }
  }

</script>



// WEBPACK FOOTER //
// src/views/base/PassMatrix.vue